/**
 * Loan reports.
 *
 * Interest is shown as finance income (lending) or finance expense
 * (borrowing), and cash movements as cash flow — both deliberately apart from
 * the business's revenue, expenses and profit, which loans never touch.
 */

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import { LOAN_DIRECTION_LABELS, percentOf, type LoanDirection } from '@cenox/shared';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { downloadFile, qs } from '../../lib/api';
import { useLocationFilter, useLocationReady } from '../../lib/locationContext';
import { money, percent, todayIn } from '../../lib/format';
import { Button, Card, CardHeader, ErrorState, Input, Spinner, cn } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { useLoanReport } from './data';
import { CategoryDonut, day } from './parts';

export function LoanReportsPage(): ReactNode {
  const { business, can } = useAuth();
  const path = useBusinessPath();
  const today = todayIn(business?.timezone ?? 'UTC');
  const [direction, setDirection] = useState<LoanDirection>('receivable');
  const [from, setFrom] = useState(`${today.slice(0, 7)}-01`);
  const [to, setTo] = useState(today);
  const location = useLocationFilter();
  const report = useLoanReport({ direction, from, to, ...location }, useLocationReady());
  const r = report.data;
  const c = r?.currencyCode ?? business?.currencyCode ?? 'NGN';

  function exportFile(dataset: 'loans' | 'loan_repayments', format: 'csv' | 'xlsx') {
    void downloadFile(
      `/api/v1${path(`/exports${qs({ dataset, format, from, to, ...location })}`)}`,
      `${dataset}.${format}`,
    );
  }

  return (
    <div className="space-y-5">
      <Link to="/loans" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="size-4" aria-hidden /> Loans
      </Link>
      <PageHeader
        title="Loan reports"
        description="Disbursements, collections, interest, overdue balances and ageing"
        action={
          can('reports:export') ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" icon={<Download className="size-4" />} onClick={() => exportFile('loans', 'xlsx')}>Loan book (Excel)</Button>
              <Button variant="secondary" icon={<Download className="size-4" />} onClick={() => exportFile('loan_repayments', 'xlsx')}>Ledger (Excel)</Button>
              <Button variant="ghost" onClick={() => exportFile('loan_repayments', 'csv')}>CSV</Button>
            </div>
          ) : undefined
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div role="radiogroup" aria-label="Lent or borrowed" className="inline-flex self-start rounded-lg border border-slate-200 bg-white p-1">
          {(['receivable', 'payable'] as const).map((d) => (
            <button
              key={d}
              type="button"
              role="radio"
              aria-checked={direction === d}
              onClick={() => setDirection(d)}
              className={cn('rounded-md px-3 py-1.5 text-sm font-medium', direction === d ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-50')}
            >
              {LOAN_DIRECTION_LABELS[d]}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 sm:w-80">
          <Input label="From" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
          <Input label="To" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>

      {report.isError ? (
        <ErrorState message="Could not load the loan report." onRetry={() => void report.refetch()} />
      ) : !r ? (
        <Spinner label="Loading report" />
      ) : (
        <>
          <section aria-label="Period totals" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Tile label={direction === 'receivable' ? 'Disbursed' : 'Borrowed'} value={money(r.disbursements.amount, c)} sub={`${r.disbursements.count} loans`} />
            <Tile label={direction === 'receivable' ? 'Collected' : 'Paid back'} value={money(r.collections.amount, c)} sub={`${money(r.collections.principal, c)} principal · ${money(r.collections.interest, c)} interest`} />
            <Tile label="Interest earned" value={money(r.interestEarned, c)} sub="finance income, on lending" tone="good" />
            <Tile label="Interest paid" value={money(r.interestPaid, c)} sub="finance expense, on borrowing" />
            <Tile label="Fees earned" value={money(r.feesEarned, c)} sub="finance income, on lending" tone="good" />
            <Tile label="Fees paid" value={money(r.feesPaid, c)} sub="finance expense, to lenders" />
            <Tile label="Bad debt" value={money(r.badDebt, c)} sub="principal written off" tone={Number(r.badDebt) > 0 ? 'warn' : 'plain'} />
            <Tile label="Interest waived" value={money(r.interestRebated, c)} sub="early settlements" />
            <Tile label="Outstanding principal" value={money(r.outstandingPrincipal, c)} sub="as of today" />
            <Tile label="Written off" value={money(r.writeOffs.amount, c)} sub={`${r.writeOffs.count} loans in the period`} tone={Number(r.writeOffs.amount) > 0 ? 'warn' : 'plain'} />
            <Tile label="Cash in" value={money(r.cashFlow.inflow, c)} sub="loan cash flow" />
            <Tile label="Cash out" value={money(r.cashFlow.outflow, c)} sub={`net ${money(r.cashFlow.net, c)}`} />
          </section>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <Card>
              <CardHeader title="Repayment performance" subtitle={`Instalments due ${day(r.from)} – ${day(r.to)}`} />
              {r.performance.due === 0 ? (
                <p className="py-6 text-center text-sm text-slate-500">No instalments fell due in this period.</p>
              ) : (
                <>
                  <p className="text-3xl font-semibold tabular-nums text-slate-900">{percent(r.performance.onTimeRate)}</p>
                  <p className="text-sm text-slate-500">paid in full by the due date</p>
                  <div className="mt-4 flex h-2.5 overflow-hidden rounded-full bg-slate-100" aria-hidden>
                    <div className="bg-emerald-500" style={{ width: `${percentOf(BigInt(r.performance.paidOnTime), BigInt(r.performance.due)) ?? 0}%` }} />
                    <div className="bg-amber-400" style={{ width: `${percentOf(BigInt(r.performance.paidLate), BigInt(r.performance.due)) ?? 0}%` }} />
                  </div>
                  <dl className="mt-3 grid grid-cols-3 gap-2 text-sm">
                    <Count label="On time" value={r.performance.paidOnTime} />
                    <Count label="Late" value={r.performance.paidLate} />
                    <Count label="Unpaid" value={r.performance.unpaid} />
                  </dl>
                </>
              )}
            </Card>

            <Card>
              <CardHeader title="Outstanding by loan type" />
              <CategoryDonut rows={r.outstandingByCategory} currency={c} />
            </Card>
          </div>

          <Card>
            <CardHeader title="Loan ageing" subtitle="Overdue balances by how late they are" />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {r.ageing.map((b) => (
                <div key={b.bucket} className={cn('rounded-lg border p-3', b.bucket === '90+' && Number(b.amount) > 0 ? 'border-rose-200 bg-rose-50' : 'border-slate-200')}>
                  <p className="text-xs font-medium text-slate-500">{b.bucket} days</p>
                  <p className="mt-1 font-semibold tabular-nums text-slate-900">{money(b.amount, c)}</p>
                  <p className="text-xs text-slate-500">{b.instalments} instalments</p>
                </div>
              ))}
            </div>
          </Card>

          <Card padded={false}>
            <div className="p-4 sm:p-5">
              <CardHeader title="Overdue instalments" subtitle={`${r.overdueInstalments.length} unpaid past their due date`} />
            </div>
            {r.overdueInstalments.length === 0 ? (
              <p className="px-5 pb-6 text-sm text-slate-500">Nothing is overdue.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-sm">
                  <thead className="border-y border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-2 font-medium">Loan</th>
                      <th className="px-3 py-2 font-medium">{direction === 'receivable' ? 'Borrower' : 'Lender'}</th>
                      <th className="px-3 py-2 font-medium">Instalment</th>
                      <th className="px-3 py-2 font-medium">Due</th>
                      <th className="px-3 py-2 text-right font-medium">Days late</th>
                      <th className="px-4 py-2 text-right font-medium">Unpaid</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {r.overdueInstalments.map((o) => (
                      <tr key={`${o.loanId}-${o.sequence}`}>
                        <td className="px-4 py-2"><Link to={`/loans/${o.loanId}`} className="text-brand-700 hover:underline">{o.reference}</Link></td>
                        <td className="max-w-[12rem] truncate px-3 py-2">{o.counterpartyName}</td>
                        <td className="px-3 py-2">#{o.sequence}</td>
                        <td className="px-3 py-2">{day(o.dueOn)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{o.daysLate}</td>
                        <td className="px-4 py-2 text-right font-medium tabular-nums">{money(o.remaining, c)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <p className="text-xs text-slate-500">
            Principal is a balance-sheet movement and never appears in revenue, expenses or profit. A payment reversed
            later is excluded from the period it was recorded in. Figures are in {c}; loans in other currencies are not
            added in.
          </p>
        </>
      )}
    </div>
  );
}

function Tile({ label, value, sub, tone = 'plain' }: { label: string; value: string; sub: string; tone?: 'plain' | 'good' | 'warn' }): ReactNode {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className={cn('mt-1 text-lg font-semibold tabular-nums', tone === 'good' && 'text-emerald-700', tone === 'warn' && 'text-amber-700', tone === 'plain' && 'text-slate-900')}>{value}</p>
      <p className="mt-0.5 text-xs text-slate-500">{sub}</p>
    </div>
  );
}

function Count({ label, value }: { label: string; value: number }): ReactNode {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="font-semibold tabular-nums text-slate-900">{value}</dd>
    </div>
  );
}
